import { Link } from "react-router-dom";
import { ArrowRight, Target, Eye, Heart, Sparkles, BadgeCheck } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";

const valueIcons = [Heart, Sparkles, BadgeCheck, Target];

const About = () => {
  const { t } = useLanguage();

  return (
    <div data-testid="about-page">
      <section className="pt-20 pb-16 md:pt-28">
        <div className="container-rotam grid lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-7">
            <p className="overline">{t.about.overline}</p>
            <h1 className="mt-4 font-heading font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              {t.about.title}
            </h1>
          </div>
          <div className="lg:col-span-5">
            <p className="text-rotam-muted leading-relaxed text-base md:text-lg">{t.about.lead}</p>
          </div>
        </div>
      </section>

      <section className="pb-24">
        <div className="container-rotam">
          <img
            src="https://images.pexels.com/photos/6694543/pexels-photo-6694543.jpeg"
            alt="ROTAM advisory"
            className="w-full h-[300px] md:h-[480px] object-cover rounded-sm"
          />
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="pb-24" data-testid="mission-vision">
        <div className="container-rotam grid md:grid-cols-2 gap-px bg-rotam-border border border-rotam-border">
          <div className="bg-white p-10 md:p-14">
            <Target size={28} strokeWidth={1.5} className="text-rotam-primary" />
            <h2 className="mt-6 font-heading text-2xl md:text-3xl font-medium">{t.about.missionTitle}</h2>
            <p className="mt-4 text-rotam-muted leading-relaxed">{t.about.missionDesc}</p>
          </div>
          <div className="bg-rotam-primary text-white p-10 md:p-14">
            <Eye size={28} strokeWidth={1.5} className="text-rotam-accent" />
            <h2 className="mt-6 font-heading text-2xl md:text-3xl font-medium">{t.about.visionTitle}</h2>
            <p className="mt-4 text-white/80 leading-relaxed">{t.about.visionDesc}</p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section-pad bg-white" data-testid="values-section">
        <div className="container-rotam">
          <p className="overline">{t.about.valuesTitle}</p>
          <h2 className="mt-4 font-heading text-3xl md:text-4xl lg:text-5xl font-light tracking-tight max-w-2xl">
            {t.about.valuesTitle}
          </h2>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-rotam-border border border-rotam-border">
            {t.about.values.map((v, i) => {
              const Icon = valueIcons[i % valueIcons.length];
              return (
                <div key={i} className="bg-white p-8 md:p-10" data-testid={`value-${i}`}>
                  <Icon size={24} strokeWidth={1.5} className="text-rotam-accent" />
                  <h3 className="mt-6 font-heading text-lg font-medium">{v.title}</h3>
                  <p className="mt-3 text-sm text-rotam-muted leading-relaxed">{v.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="section-pad" data-testid="team-section">
        <div className="container-rotam">
          <p className="overline">{t.about.teamTitle}</p>
          <h2 className="mt-4 font-heading text-3xl md:text-4xl lg:text-5xl font-light tracking-tight">
            {t.about.teamTitle}
          </h2>
          <div className="mt-14 grid md:grid-cols-3 gap-8">
            {t.about.team.map((m, i) => (
              <div
                key={i}
                data-testid={`team-${i}`}
                className="bg-white border border-rotam-border p-8 md:p-10 rounded-sm hover:-translate-y-1 transition-transform duration-300"
              >
                {m.photo ? (
                  <div className="w-24 h-24 rounded-full overflow-hidden bg-rotam-bg border border-rotam-border">
                    <img
                      src={m.photo}
                      alt={m.name}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                ) : (
                  <div className="w-24 h-24 bg-rotam-primary/10 text-rotam-primary flex items-center justify-center rounded-full font-heading text-2xl font-medium">
                    {m.name
                      .split(" ")
                      .map((p) => p[0])
                      .slice(0, 2)
                      .join("")}
                  </div>
                )}
                <h3 className="mt-6 font-heading text-xl font-medium">{m.name}</h3>
                <p className="mt-1 text-xs tracking-[0.18em] uppercase text-rotam-accent">{m.role}</p>
                <p className="mt-4 text-sm text-rotam-muted leading-relaxed">{m.bio}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-pad bg-rotam-primary text-white">
        <div className="container-rotam grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <h2 className="font-heading text-3xl md:text-4xl font-light leading-tight">
              {t.home.ctaTitle}
            </h2>
          </div>
          <div className="md:col-span-4 md:flex md:justify-end">
            <Link to="/contact" data-testid="about-cta" className="inline-flex items-center gap-2 bg-rotam-accent text-rotam-primary px-8 py-4 rounded-sm font-medium hover:bg-white transition-colors">
              {t.home.ctaButton} <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
